/**
 * The provider app's "Join" action for a virtual visit.
 *
 * It opens the visit's room on the in-page call surface — `ConferencingHost`,
 * mounted once in `GlobalOverlays` — rather than sending the clinician to a
 * vendor tab. That makes this a one-line handler, and the collapse is the point:
 *
 *   - NO POPUP. The Meet path had to claim a tab synchronously before any await,
 *     because a `window.open` after an await is no longer attributable to the
 *     click's user gesture and Safari/Firefox refuse it silently. Nothing here
 *     opens a window, so none of that applies.
 *   - NO RESOLVE, NO ATTACH. There is no URI to fetch fresh and no space to mint
 *     first. `openVisitRoom` records intent in the call store; the host asks the
 *     server for a host credential and owns the session.
 *   - NO IN-FLIGHT STATE. Entering is a synchronous store send, so there is
 *     nothing to debounce and no button to leave disabled. Re-entering the room
 *     already active is idempotent and deliberately does not un-minimise it.
 *   - NO REFUSAL HANDLING HERE. A refused credential is the host's, and it
 *     surfaces through the same global tRPC error-link toast as before.
 *
 * The clinician may open the room while the visit is still `booked` and wait in
 * it; admitting the patient happens from inside the call surface, not from here.
 */
import type { VisitDto } from "@repo/data-store";
import { Button } from "@repo/ui/components/ui/button";
import { VideoIcon } from "lucide-react";
import { openVisitRoom } from "@/features/conferencing";

export interface JoinVisitButtonProps {
  visit: VisitDto;
  /** Ghost camera icon instead of a filled button, for dense action rows. */
  iconOnly?: boolean;
}

export function JoinVisitButton({ visit, iconOnly = false }: JoinVisitButtonProps) {
  if (visit.encounterClass !== "VR") return null;

  const join = () => openVisitRoom(visit.visitId);

  if (iconOnly) {
    return (
      <Button variant="ghost" size="icon-xs" onClick={join} aria-label="Join visit">
        <VideoIcon className="size-4" />
      </Button>
    );
  }

  return <Button onClick={join}>Join</Button>;
}
